import { nanoid } from 'nanoid';
import { db } from './index';
import { PALETTE, DEAL_TO_FUNNEL, FUNNEL_TO_DEAL, FUNNEL_BY_KEY, DEMO_CURRENT_USER_NAME, type Deal, type DealStatus, type DealTransaction } from '@/types';

let seedPromise: Promise<void> | null = null;

// Версия демо-данных. Меняем при правках seed — тогда данные пересеваются автоматически.
const SEED_VERSION = '14';

export async function seedIfEmpty(): Promise<void> {
  if (seedPromise) return seedPromise;
  seedPromise = (async () => {
    const tables = [db.branches, db.employees, db.teams, db.sources, db.occasions, db.game_formats, db.refusal_reasons, db.deals];
    await db.transaction('rw', tables, async () => {
      const empty = (await db.branches.count()) === 0;
      const stored = localStorage.getItem('seed_version');
      if (!empty && stored === SEED_VERSION) return;
      // Пересев: чистим демо-таблицы (настройки не трогаем) и заполняем заново.
      for (const t of tables) await t.clear();
      await runSeed();
      localStorage.setItem('seed_version', SEED_VERSION);
    });
  })();
  return seedPromise;
}

// 28 филиалов из ТЗ — города КЗ/РФ/прочее, с местными часовыми поясами.
// Филиалы по эталону (6 городов). Баку — UTC+4, остальные — UTC+5.
const BRANCH_DEF: { name: string; tzOffMin: number; currency?: 'KZT' | 'UZS' | 'AZN' | 'RUB' }[] = [
  { name: 'Алматы',            tzOffMin: 5 * 60 },
  { name: 'Астана',            tzOffMin: 5 * 60 },
  { name: 'Баку',              tzOffMin: 4 * 60 },
  { name: 'Караганда',         tzOffMin: 5 * 60 },
  { name: 'Ташкент',           tzOffMin: 5 * 60 },
  { name: 'Шымкент',           tzOffMin: 5 * 60 },
  // Дом-филиал ивента: ивент-сделки живут здесь (branch_id), а в календаре городов
  // показываются зеркалом (mirror_branch_id). На дашборде выбор «Город проведения»
  // появляется, когда выбран именно этот филиал.
  { name: 'Ивент',             tzOffMin: 5 * 60 },
];

const FIRST_NAMES = ['Ангелина', 'Ирина', 'Михаил', 'Олег', 'Анна', 'Сергей', 'Мария', 'Дмитрий', 'Алексей', 'Татьяна', 'Игорь', 'Светлана', 'Виктор', 'Наталья', 'Артём', 'Ксения', 'Богдан', 'Егор', 'Юлия', 'Владислав'];
const LAST_NAMES = ['Мельникова', 'Сапрова', 'Ветлугин', 'Балакин', 'Громов', 'Иванова', 'Петров', 'Кузнецов', 'Соколов', 'Орлов', 'Морозов', 'Зайцева', 'Лебедев', 'Новикова', 'Волков'];
const COMPANY_NAMES = ['ТОО МОНТ-ТРЕЙД', 'ТОО Барс-Сервис', 'ИП Тимофеев', 'Wellness Studio', 'ТОО Sun-Tech', 'Магнолия Group', 'Capella Events', 'ТОО АНИКА', 'ИП Кравцова', 'Lumiere SPA'];

const OCC_TYPES = ['День рождения', 'Свадьба', 'Тусау кесер', 'ДР компании', 'Корпоратив', 'Тимбилдинг', 'Юбилей'];
// Источники «Откуда узнали» — как в эталоне ShowToday.
const SOURCE_NAMES = ['Instagram таргет', 'Instagram блогер', 'Google Ads', 'От друзей', 'От партнеров', 'Тик-Ток', 'Другое', 'LTV', 'Гео сервисы (2gis)'];
// Форматы игр — как в эталоне (значок + название).
const FORMATS: { name: string; emoji: string }[] = [
  { name: 'Мафия', emoji: '🕵️' },
  { name: 'Black', emoji: '😈' },
  { name: 'Kids', emoji: '🧒' },
  { name: 'Музыкальная программа', emoji: '🎵' },
  { name: 'Классика', emoji: '🎭' },
  { name: 'Тимбилдинг', emoji: '🤝' },
  { name: 'Вечеринка', emoji: '🎉' },
  { name: 'Эвент мероприятие', emoji: '🎤' },
  { name: 'Ключ', emoji: '🆔' },
  { name: 'Активный тимбилдинг', emoji: '🏃' },
];
const EMOJIS = ['🎂', '🎉', '🚗', '🎤', '💍', '🎭', '🏆', '🎁', '🎊', '🍰', '🥳'];
const REFUSE_REASONS = ['Дорого', 'Не подошла дата', 'Передумали', 'Не вышли на связь', 'Конкуренты'];

function pick<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)]; }
function pickN<T>(arr: T[], n: number): T[] {
  const c = [...arr]; const out: T[] = [];
  for (let i = 0; i < n && c.length; i++) out.push(c.splice(Math.floor(Math.random() * c.length), 1)[0]);
  return out;
}

function randPhone(): string {
  const a = 700 + Math.floor(Math.random() * 99);
  const b = String(Math.floor(Math.random() * 999)).padStart(3, '0');
  const c = String(Math.floor(Math.random() * 99)).padStart(2, '0');
  const d = String(Math.floor(Math.random() * 99)).padStart(2, '0');
  return `+7 (${a}) ${b}-${c}-${d}`;
}

async function runSeed(): Promise<void> {
  const now = Date.now();
  const today0 = new Date(); today0.setHours(0, 0, 0, 0); const todayMs = today0.getTime();

  // Сезонность по месяцам: объём (кол-во игр) и средний чек гуляют НЕЗАВИСИМО друг от друга,
  // чтобы на дашборде линии «Сумма / Количество / Средний чек» расходились, а не шли впритык.
  // k = 0 — текущий месяц, отрицательные — прошлые. Лёгкий всплеск ~5 мес назад (как на эталоне слева).
  const baseMonthKey = today0.getFullYear() * 12 + today0.getMonth();
  // Явные множители для видимого окна (последние 6 мес: k = -5…0): всплеск слева + пересечения линий.
  // k=-5 — крайний левый месяц на графике (как декабрьский всплеск на эталоне).
  const VOL_BY_K: Record<number, number> = { [-5]: 1.85, [-4]: 1.0, [-3]: 1.3, [-2]: 0.75, [-1]: 1.15, [0]: 0.68 };
  const PRICE_BY_K: Record<number, number> = { [-5]: 1.4, [-4]: 0.88, [-3]: 1.06, [-2]: 1.22, [-1]: 0.92, [0]: 1.16 };
  const seasonOf = (ts: number) => {
    const d = new Date(ts);
    const k = (d.getFullYear() * 12 + d.getMonth()) - baseMonthKey;
    const vol = VOL_BY_K[k] ?? Math.max(0.5, 0.9 + 0.4 * Math.sin(k * 0.8 + 0.6));
    const price = PRICE_BY_K[k] ?? Math.max(0.7, 0.95 + 0.35 * Math.sin(k * 0.5 + 2.1));
    return { vol, price };
  };

  // ── Филиалы ──
  const branches = BRANCH_DEF.map((b, i) => ({
    id: nanoid(),
    name: b.name,
    city: b.name,
    color: PALETTE[i % PALETTE.length],
    tz_offset_minutes: b.tzOffMin,
  }));
  await db.branches.bulkPut(branches);

  // ── Сотрудники: по 3-6 на филиал (богатая база для поиска) ──
  type EmployeeRole = 'manager' | 'event_manager' | 'host' | 'admin' | 'tech';
  const employees: {
    id: string; full_name: string; branch_id: string; role: EmployeeRole;
    is_active: boolean; phone?: string; email?: string;
  }[] = [];

  for (const b of branches) {
    // Обязательный менеджер
    const m1 = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
    employees.push({
      id: nanoid(),
      full_name: m1,
      branch_id: b.id,
      role: 'manager',
      is_active: true,
      phone: randPhone(),
      email: `${m1.split(' ')[0].toLowerCase()}@${b.name.replace(/[^a-zA-Zа-яА-Я]/g, '').toLowerCase()}.com`,
    });
    // Event-менеджер
    if (Math.random() > 0.3) {
      const name = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
      employees.push({
        id: nanoid(), full_name: name, branch_id: b.id, role: 'event_manager',
        is_active: true, phone: randPhone(),
      });
    }
    // 1-3 ведущих
    const hostCount = 1 + Math.floor(Math.random() * 3);
    for (let i = 0; i < hostCount; i++) {
      employees.push({
        id: nanoid(), full_name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
        branch_id: b.id, role: 'host', is_active: true, phone: randPhone(),
      });
    }
    // Админ
    if (Math.random() > 0.5) {
      employees.push({
        id: nanoid(), full_name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
        branch_id: b.id, role: 'admin', is_active: true, phone: randPhone(),
      });
    }
    // Технический специалист
    if (Math.random() > 0.6) {
      employees.push({
        id: nanoid(), full_name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
        branch_id: b.id, role: 'tech', is_active: true, phone: randPhone(),
      });
    }
  }

  // Демо-«текущий пользователь» (host в Алматы) — для тоггла «показывать только где назначен».
  // После Этапа 1 (вход) заменится реальным вошедшим сотрудником.
  const meBranch = branches.find((b) => b.name === 'Алматы') ?? branches[0];
  const me = {
    id: nanoid(), full_name: DEMO_CURRENT_USER_NAME, branch_id: meBranch.id,
    role: 'host' as EmployeeRole, is_active: true, phone: randPhone(), email: 'valera@almaty.com',
  };
  employees.push(me);

  await db.employees.bulkPut(employees);

  // ── Команды: до 5 на каждый филиал, шаблонные названия «Команда 1…5» ──
  const teams: { id: string; name: string; color: string; branch_id: string | null; member_ids: string[]; is_archived: boolean; order: number }[] = [];
  let teamColorIdx = 0;
  const pickColor = () => PALETTE[(teamColorIdx++) % PALETTE.length];

  for (const b of branches) {
    const branchEmployees = employees.filter((e) => e.branch_id === b.id);
    if (branchEmployees.length === 0) continue;
    for (let i = 1; i <= 5; i++) {
      teams.push({
        id: nanoid(),
        name: `Команда ${i}`,
        color: pickColor(),
        branch_id: b.id,
        member_ids: pickN(branchEmployees, Math.min(2 + Math.floor(Math.random() * 3), branchEmployees.length)).map((e) => e.id),
        is_archived: false,
        order: i,
      });
    }
  }

  await db.teams.bulkPut(teams);

  // Команда для сделки берётся ТОЛЬКО из её филиала (иначе сделка выпадает в виде «Ресурсы»,
  // где строки — команды текущего филиала).
  const pickBranchTeam = (branchId: string) => {
    const list = teams.filter((t) => t.branch_id === branchId);
    return list.length ? pick(list) : pick(teams);
  };

  // ── Справочники ──
  const sources = SOURCE_NAMES.map((n) => ({ id: nanoid(), name: n }));
  await db.sources.bulkPut(sources);

  const occasions = OCC_TYPES.map((n) => ({
    id: nanoid(),
    name: n,
    yearly_recurring: n === 'День рождения' || n === 'Юбилей',
  }));
  await db.occasions.bulkPut(occasions);

  const game_formats = FORMATS.map((f) => ({ id: nanoid(), name: f.name, emoji: f.emoji }));
  await db.game_formats.bulkPut(game_formats);

  const refusal_reasons = REFUSE_REASONS.map((n) => ({ id: nanoid(), name: n }));
  await db.refusal_reasons.bulkPut(refusal_reasons);

  // ── Сделки: ~80 штук вокруг сегодня (±21 день) ──
  const STATUSES: DealStatus[] = ['new', 'in_work', 'paid_partial', 'paid', 'done', 'refused'];
  const STATUS_WEIGHTS = [0.10, 0.25, 0.15, 0.20, 0.25, 0.05];

  function weightedStatus(): DealStatus {
    let r = Math.random();
    for (let i = 0; i < STATUSES.length; i++) {
      r -= STATUS_WEIGHTS[i];
      if (r <= 0) return STATUSES[i];
    }
    return 'new';
  }

  let dealCounter = 74400;
  const deals: Deal[] = [];

  // Сотрудник филиала → роль на игре (для блока «Персонал» в карточке / графика).
  const STAFF_ROLE: Record<EmployeeRole, string> = {
    manager: 'vedushiy', event_manager: 'sovedushiy', host: 'vedushiy', admin: 'hostes', tech: 'didjey',
  };
  // Назначить 2–4 человек из филиала; демо-пользователь Валера попадает в ~55% игр своего филиала.
  const makeStaff = (branchId: string) => {
    const pool = employees.filter((e) => e.branch_id === branchId);
    const chosen = pickN(pool, Math.min(2 + Math.floor(Math.random() * 3), pool.length));
    if (branchId === me.branch_id && !chosen.some((e) => e.id === me.id) && Math.random() > 0.45) chosen.unshift(me);
    return chosen.map((e, idx) => ({ employee_id: e.id, role: idx === 0 ? 'vedushiy' : STAFF_ROLE[e.role] }));
  };

  for (let dayOff = -21; dayOff <= 21; dayOff++) {
    const dayBase = todayMs + dayOff * 24 * 3600_000;
    const season = seasonOf(dayBase);
    const dealsPerDay = Math.max(1, Math.round((Math.floor(Math.random() * 3) + 1) * season.vol)); // 1..3 × сезон
    for (let k = 0; k < dealsPerDay; k++) {
      const status = dayOff < -2 && Math.random() > 0.3 ? 'done' : weightedStatus();
      const branch = pick(branches);
      const team = pickBranchTeam(branch.id);
      const occ = pick(occasions);
      const fmt = pick(game_formats);
      const src = pick(sources);
      const hour = 10 + Math.floor(Math.random() * 12); // 10..21
      const duration = pick([60, 90, 120, 180]);
      const startAt = dayBase + hour * 3600_000;
      const endAt = startAt + duration * 60_000;
      const placeIsOutdoor = Math.random() > 0.4;
      const amount = Math.round((Math.floor(Math.random() * 50) + 5) * season.price) * 10_000; // 50k..550k × сезон
      const prepay = Math.random() > 0.4 ? Math.floor(amount * (Math.random() * 0.5 + 0.3)) : undefined;
      const remainder = status === 'paid' ? amount - (prepay ?? 0) : undefined;
      dealCounter += Math.floor(Math.random() * 3) + 1;

      const contact = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
      const isB2B = Math.random() > 0.7;
      const contactName = isB2B ? pick(COMPANY_NAMES) : contact;

      // Финансовые операции (если игра проведена)
      const transactions: DealTransaction[] = [];
      if (status === 'paid' || status === 'done') {
        transactions.push({
          id: nanoid(), article: 'Оплата за игру', counterparty: contact,
          account: pick(['cash', 'card', 'qr']), amount: amount - (prepay ?? 0),
          accrued_at: endAt, accrued_done: true, cleared_at: endAt + 30 * 60_000, cleared_done: true,
        });
        if (prepay) {
          transactions.push({
            id: nanoid(), article: 'Предоплата за игру', counterparty: contact,
            account: 'qr', amount: prepay,
            accrued_at: startAt - 7 * 24 * 3600_000, accrued_done: true,
            cleared_at: startAt - 7 * 24 * 3600_000 + 3600_000, cleared_done: true,
          });
        }
        // Расходы (3-5 строк)
        const expenses: Array<[string, number]> = [
          ['Ведущий', -(1000 + Math.floor(Math.random() * 500))],
          ['Соведущий', -(300 + Math.floor(Math.random() * 200))],
          ['Диджей', -(700 + Math.floor(Math.random() * 300))],
        ];
        if (Math.random() > 0.5) expenses.push(['Декоратор', -(500 + Math.floor(Math.random() * 300))]);
        if (Math.random() > 0.7) expenses.push(['Приход по бару', 240 + Math.floor(Math.random() * 500)]);
        for (const [art, sum] of expenses) {
          transactions.push({
            id: nanoid(), article: art, counterparty: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
            account: 'cash', amount: sum,
            accrued_at: startAt, accrued_done: true,
            cleared_at: endAt + 60 * 60_000, cleared_done: true,
          });
        }
      }

      const deal: Deal = {
        id: nanoid(),
        number: String(dealCounter),
        kind: Math.random() > 0.3 ? 'b2c' : 'event',
        amount,
        currency: 'KZT',
        contact_name: contactName,
        contact_phone: randPhone(),
        branch_id: branch.id,
        responsible_id: employees.find((e) => e.branch_id === branch.id)?.id ?? null,
        source_id: src.id,
        type_tag: isB2B ? 'юр.лицо' : 'физ.лицо',
        status,
        created_at: startAt - (Math.floor(Math.random() * 14) + 3) * 24 * 3600_000,
        taken_at: startAt - (Math.floor(Math.random() * 14) + 3) * 24 * 3600_000,
        payment: {
          prepayment_amount: prepay,
          prepayment_receipts: prepay ? [{
            id: nanoid(),
            filename: `№64_${pick(COMPANY_NAMES).slice(0, 8)}_${new Date(startAt - 7 * 24 * 3600_000).toLocaleDateString('ru-RU').split('.').join('_')}.pdf`,
            mime: 'application/pdf', size: 124_000 + Math.floor(Math.random() * 100_000),
            uploaded_at: startAt - 7 * 24 * 3600_000,
          }] : [],
          remainder_amount: remainder,
          remainder_receipts: [],
        },
        event: {
          occasion_id: occ.id,
          event_date: dayBase,
          start_at: startAt, end_at: endAt,
          place: placeIsOutdoor ? 'outside' : (Math.random() > 0.5 ? 'studio_rest' : 'studio'),
          address: placeIsOutdoor ? `${branch.name}, ул. ${pick(['Абая', 'Толе би', 'Ленина', 'Кенесары', 'Тимирязева'])}, ${1 + Math.floor(Math.random() * 200)}` : '',
          participant_count: 8 + Math.floor(Math.random() * 50),
          participant_age_min: 18 + Math.floor(Math.random() * 30),
          participant_age_max: 25 + Math.floor(Math.random() * 35),
          game_format_id: fmt.id,
          played_before: Math.random() > 0.6,
          duration_minutes: duration,
          language: pick(['Русский', 'Казахский', 'Английский']),
          team_text: '',
          uses_invoice_account: isB2B,
          extra_distance_fee: placeIsOutdoor && Math.random() > 0.6 ? 5000 : 0,
          responsible_name: isB2B ? contact : '',
          responsible_phone: isB2B ? randPhone() : '',
          emoji: pick(EMOJIS),
          comments: pick([
            'Оплата по р/с, договор готов.\nПодготовить сценарий заранее.',
            'Гость просил больше активностей. Музыка — поп/казахская.',
            'Предоплата внесена. Согласовать ведущего за день до игры.',
            'Зал закрытый под них, посчитали до 16 чел.',
            'Клиент постоянный — сделать акцент на сервис.',
          ]),
        },
        transactions,
        staff_assignments: makeStaff(branch.id),
        history: [],
        team_id: team.id,
        updated_at: now,
      };

      // Детальная история: «Добавлена новая игра» со всеми параметрами + правка с трекингом редактора.
      {
        const ev = deal.event;
        const created = deal.created_at;
        const fStat = deal.funnel_status ?? DEAL_TO_FUNNEL[deal.status];
        const initial = ([
          ['Язык', ev.language],
          ['Откуда узнали', src.name],
          ['Место проведения', ev.place === 'outside' ? 'Выезд' : 'Студия'],
          ['Формат игры', fmt.name],
          ['Повод', occ.name],
          ['Возраст участников', ev.participant_age_min && ev.participant_age_max ? `${ev.participant_age_min}-${ev.participant_age_max} лет` : undefined],
          ['Кол-во участников', ev.participant_count != null ? String(ev.participant_count) : undefined],
          ['Играли ранее', ev.played_before ? 'да' : 'нет'],
          ['Длительность игры', ev.duration_minutes != null ? String(ev.duration_minutes) : undefined],
          ['Адрес', ev.address || undefined],
          ['Доплата за дальность', ev.extra_distance_fee ? String(ev.extra_distance_fee) : undefined],
          ['Предоплата', deal.payment.prepayment_amount != null ? String(deal.payment.prepayment_amount) : undefined],
          ['Контакты', `${deal.contact_name} : ${deal.contact_phone ?? ''}`],
        ] as [string, string | undefined][])
          .filter(([, v]) => v)
          .map(([label, to]) => ({ label, to: to as string }));
        deal.history = [
          { ts: created, user: 'Ирина Мельникова', action: 'Добавлена новая игра', changes: initial },
          {
            ts: created + 4 * 24 * 3600_000, user: 'Владислав Гармаев', action: 'Изменена информация об игре',
            changes: [
              { label: 'Редактор', from: 'Ирина Мельникова', to: 'Владислав Гармаев' },
              { label: 'Статус', kind: 'status' as const, from: 'Предбронь', to: FUNNEL_BY_KEY.get(fStat)?.label },
            ],
          },
        ];
      }

      // Ответы ОКК — для части проведённых/оплаченных игр (как в эталоне «Формы → действие»).
      if ((status === 'done' || status === 'paid') && Math.random() > 0.4) {
        const okkTs = endAt + 24 * 3600_000;
        deal.okk = {
          created_by: 'Айдана Садыкова', created_at: okkTs, updated_by: 'Айдана Садыкова', updated_at: okkTs,
          answers: {
            source: pick(['instagram', 'От друзей', '2GIS', 'Tik-Tok', 'блогер']),
            communication: 'Да',
            quality: pick(['10', '9', '8', '10', '10']),
            wishes: pick(['все хорошо', 'все супер', 'все понравилось', 'все отлично', '']),
            recommend: '10',
            participants: pick(['рс', '145 перев 25 по', '130 нал 25 по', '164 перев 25 по', '110 перев 25по']),
            payment_type: pick(['перевод', 'наличными', 'терминал']),
            on_time: 'Да',
            future_contact: pick(['Да', 'Нет']),
          },
        };
      }

      if (status === 'refused') {
        deal.refusal = {
          reason_id: pick(refusal_reasons).id,
          refused_at: startAt - 3 * 24 * 3600_000,
          comment: pick(['Бюджет не подошёл', 'Перенесли на следующий месяц', 'Уехали в отпуск', '']),
        };
      }

      deals.push(deal);
    }
  }

  // ── 15 демо-игр на ОДИН день: 15-е число ТЕКУЩЕГО месяца ──
  // Чтобы на стенде было видно, как выглядит окно «список дня» с реалистичным объёмом.
  // День вычисляется от «сейчас» (без хардкода года/месяца): setDate(15) на текущем месяце.
  {
    const listDay0 = new Date();
    listDay0.setDate(15);
    listDay0.setHours(0, 0, 0, 0);
    const listDayMs = listDay0.getTime();
    const LIST_STATUSES: DealStatus[] = ['new', 'in_work', 'paid_partial', 'paid', 'done', 'refused'];
    for (let i = 0; i < 15; i++) {
      const branch = pick(branches);
      // ~2/3 игр с командой, каждая третья — без команды.
      const team = i % 3 === 0 ? null : pickBranchTeam(branch.id);
      const occ = pick(occasions);
      const fmt = game_formats[i % game_formats.length];      // проходим все форматы по кругу
      const src = pick(sources);
      const status = LIST_STATUSES[i % LIST_STATUSES.length]; // разные статусы по кругу
      // Время начала: с 10:00 до ~21:12 шагом ~48 мин; длительность 90/120 мин.
      const startMin = 10 * 60 + i * 48;
      const duration = i % 2 === 0 ? 120 : 90;
      const startAt = listDayMs + startMin * 60_000;
      const endAt = startAt + duration * 60_000;
      const isB2B = i % 4 === 0;
      const contact = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
      const amount = (8 + i) * 10_000;                        // 80k … 220k
      const prepay = status !== 'new' && status !== 'refused' ? Math.floor(amount * 0.4) : undefined;
      const remainder = status === 'paid' ? amount - (prepay ?? 0) : undefined;
      dealCounter += 1;

      const deal: Deal = {
        id: nanoid(),
        number: String(dealCounter),
        kind: isB2B ? 'event' : 'b2c',
        amount,
        currency: 'KZT',
        contact_name: isB2B ? pick(COMPANY_NAMES) : contact,
        contact_phone: randPhone(),
        branch_id: branch.id,
        responsible_id: employees.find((e) => e.branch_id === branch.id)?.id ?? null,
        source_id: src.id,
        type_tag: isB2B ? 'юр.лицо' : 'физ.лицо',
        status,
        created_at: startAt - 5 * 24 * 3600_000,
        taken_at: startAt - 5 * 24 * 3600_000,
        payment: {
          prepayment_amount: prepay,
          prepayment_receipts: [],
          remainder_amount: remainder,
          remainder_receipts: [],
        },
        event: {
          occasion_id: occ.id,
          event_date: listDayMs,
          start_at: startAt,
          end_at: endAt,
          place: i % 2 === 0 ? 'studio' : 'outside',
          address: i % 2 === 0 ? '' : `${branch.name}, ул. ${pick(['Абая', 'Толе би', 'Кенесары', 'Тимирязева'])}, ${1 + i * 7}`,
          participant_count: 8 + i * 2,
          game_format_id: fmt.id,
          duration_minutes: duration,
          language: pick(['Русский', 'Казахский', 'Английский']),
          emoji: EMOJIS[i % EMOJIS.length],
        },
        transactions: [],
        staff_assignments: makeStaff(branch.id),
        history: [{ ts: startAt - 5 * 24 * 3600_000, action: 'Создано' }],
        team_id: team ? team.id : null,
        updated_at: now,
      };

      if (status === 'refused') {
        deal.refusal = {
          reason_id: pick(refusal_reasons).id,
          refused_at: startAt - 2 * 24 * 3600_000,
          comment: pick(['Бюджет не подошёл', 'Перенесли дату', '']),
        };
      }

      deals.push(deal);
    }
  }

  // ── Ивент-филиал + ивент-игры с зеркалом в города (для фильтра «Город проведения») ──
  // Ивент-сделки (kind==='event') лежат в дом-филиале «Ивент», а mirror_branch_id указывает
  // на РАЗНЫЕ городские филиалы. На дашборде при выборе филиала «Ивент» появляется выбор
  // города, и цифры меняются при переключении. Id городов берём из уже созданных branch —
  // строки не хардкодим.
  {
    const eventBranch = branches.find((b) => b.name === 'Ивент') ?? branches[branches.length - 1];
    const mirrorCities = branches.filter((b) => b.id !== eventBranch.id).slice(0, 2); // города-зеркала: Алматы, Астана
    const EVENT_STATUSES: DealStatus[] = ['paid', 'done', 'in_work', 'done', 'paid', 'done', 'in_work', 'done'];
    for (let i = 0; i < 8; i++) {
      const city = mirrorCities[i % mirrorCities.length];
      const status = EVENT_STATUSES[i % EVENT_STATUSES.length];
      const dayOff = (i % 12) - 6;                       // от −6 до +5 дней вокруг сегодня
      const dayBase = todayMs + dayOff * 24 * 3600_000;
      const hour = 12 + (i % 8);
      const duration = pick([120, 180]);
      const startAt = dayBase + hour * 3600_000;
      const endAt = startAt + duration * 60_000;
      const amount = (30 + i * 8) * 10_000;             // 300k … 860k — заметные суммы для дашборда
      const prepay = Math.floor(amount * 0.5);
      const remainder = status === 'paid' || status === 'done' ? amount - prepay : undefined;
      const fmt = pick(game_formats);
      const occ = pick(occasions);
      const src = pick(sources);
      dealCounter += 1;

      deals.push({
        id: nanoid(),
        number: String(dealCounter),
        kind: 'event',
        amount,
        currency: 'KZT',
        contact_name: pick(COMPANY_NAMES),
        contact_phone: randPhone(),
        branch_id: eventBranch.id,
        mirror_branch_id: city.id,
        responsible_id: employees.find((e) => e.branch_id === eventBranch.id)?.id ?? null,
        source_id: src.id,
        type_tag: 'юр.лицо',
        status,
        created_at: startAt - 10 * 24 * 3600_000,
        taken_at: startAt - 10 * 24 * 3600_000,
        payment: {
          prepayment_amount: prepay,
          prepayment_receipts: [],
          remainder_amount: remainder,
          remainder_receipts: [],
        },
        event: {
          occasion_id: occ.id,
          event_date: dayBase,
          start_at: startAt, end_at: endAt,
          place: 'outside',
          address: `${city.name}, ул. ${pick(['Абая', 'Толе би', 'Кенесары'])}, ${10 + i * 5}`,
          participant_count: 50 + i * 10,
          game_format_id: fmt.id,
          duration_minutes: duration,
          language: pick(['Русский', 'Казахский']),
          uses_invoice_account: true,
          emoji: '🎤',
        },
        transactions: [],
        staff_assignments: makeStaff(eventBranch.id),
        history: [{ ts: startAt - 10 * 24 * 3600_000, action: 'Создано' }],
        team_id: null,
        updated_at: now,
      });
    }
  }

  // ── Историческая «фоновая» база (≈13 мес назад … −22 дня) ──
  // Нужна для графика «Продажи за полгода» и сравнения год-к-году в KPI.
  // Лёгкие сделки: без транзакций/детальной истории/ОКК, но с суммой/статусом/датами.
  for (let dayOff = -400; dayOff <= -22; dayOff++) {
    const dayBase = todayMs + dayOff * 24 * 3600_000;
    const season = seasonOf(dayBase);
    const perDay = Math.max(1, Math.round((2 + Math.floor(Math.random() * 3)) * season.vol)); // 2..4 × сезон
    for (let k = 0; k < perDay; k++) {
      const status: DealStatus = Math.random() > 0.12 ? 'done' : pick(['refused', 'paid', 'in_work'] as DealStatus[]);
      const branch = pick(branches);
      const team = pickBranchTeam(branch.id);
      const fmt = pick(game_formats);
      const src = pick(sources);
      const occ = pick(occasions);
      const hour = 10 + Math.floor(Math.random() * 12);
      const duration = pick([60, 90, 120, 180]);
      const startAt = dayBase + hour * 3600_000;
      const endAt = startAt + duration * 60_000;
      const placeIsOutdoor = Math.random() > 0.5;
      const amount = Math.round((Math.floor(Math.random() * 50) + 5) * season.price) * 10_000;
      dealCounter += Math.floor(Math.random() * 3) + 1;
      const contact = `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`;
      const isB2B = Math.random() > 0.75;
      deals.push({
        id: nanoid(),
        number: String(dealCounter),
        kind: isB2B ? 'event' : 'b2c',
        amount,
        currency: 'KZT',
        contact_name: isB2B ? pick(COMPANY_NAMES) : contact,
        contact_phone: randPhone(),
        branch_id: branch.id,
        responsible_id: employees.find((e) => e.branch_id === branch.id)?.id ?? null,
        source_id: src.id,
        type_tag: isB2B ? 'юр.лицо' : 'физ.лицо',
        status,
        created_at: startAt - (Math.floor(Math.random() * 14) + 3) * 24 * 3600_000,
        taken_at: startAt - 3 * 24 * 3600_000,
        payment: { prepayment_receipts: [], remainder_receipts: [] },
        event: {
          occasion_id: occ.id,
          event_date: dayBase,
          start_at: startAt, end_at: endAt,
          place: placeIsOutdoor ? 'outside' : (Math.random() > 0.5 ? 'studio_rest' : 'studio'),
          address: placeIsOutdoor ? `${branch.name}, ул. ${pick(['Абая', 'Толе би', 'Ленина', 'Кенесары'])}, ${1 + Math.floor(Math.random() * 200)}` : '',
          participant_count: 8 + Math.floor(Math.random() * 50),
          game_format_id: fmt.id,
          played_before: Math.random() > 0.6,
          duration_minutes: duration,
          language: pick(['Русский', 'Казахский', 'Английский']),
          emoji: pick(EMOJIS),
        },
        transactions: [],
        staff_assignments: makeStaff(branch.id),
        history: [{ ts: startAt, action: 'Создано' }],
        team_id: team.id,
        updated_at: now,
      });
    }
  }

  // ── Демо: сделки «Без менеджера» в ТЕКУЩЕЙ неделе/месяце ──
  // Ответственный НЕ назначен (responsible_id = null) — ровно тот критерий, по которому
  // дашборд и отчёт относят сделку в группу «Без менеджера». Даты — в текущей неделе
  // (плитка/отчёт по умолчанию показывают неделю), разные стадии — чтобы было что открыть.
  {
    const wd = (today0.getDay() + 6) % 7;                 // Пн = 0
    const weekStart = todayMs - wd * 24 * 3600_000;
    const NO_MGR: { funnel: 'prebooking' | 'booking' | 'done' | 'booking_barter'; amount: number; b2b: boolean }[] = [
      { funnel: 'prebooking',     amount: 180_000, b2b: false },
      { funnel: 'booking',        amount: 320_000, b2b: false },
      { funnel: 'done',           amount: 450_000, b2b: true  },
      { funnel: 'booking_barter', amount: 260_000, b2b: false },
    ];
    // Ставим на будни текущей недели (вт..пт), чтобы гарантированно попасть в текущую неделю.
    NO_MGR.forEach((cfg, i) => {
      const branch = branches.filter((b) => b.name !== 'Ивент')[i % (branches.length - 1)] ?? branches[0];
      const fmt = game_formats[i % game_formats.length];
      const occ = pick(occasions);
      const src = pick(sources);
      const dayBase = weekStart + (i + 1) * 24 * 3600_000; // вт, ср, чт, пт
      const hour = 13 + i;                                 // 13..16
      const startAt = dayBase + hour * 3600_000;
      const duration = 120;
      const endAt = startAt + duration * 60_000;
      const status: DealStatus = FUNNEL_TO_DEAL[cfg.funnel];
      const prepay = cfg.funnel === 'prebooking' ? undefined : Math.floor(cfg.amount * 0.4);
      const remainder = cfg.funnel === 'done' ? cfg.amount - (prepay ?? 0) : undefined;
      dealCounter += 1;
      deals.push({
        id: nanoid(),
        number: String(dealCounter),
        kind: cfg.b2b ? 'event' : 'b2c',
        amount: cfg.amount,
        currency: 'KZT',
        contact_name: cfg.b2b ? pick(COMPANY_NAMES) : `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
        contact_phone: randPhone(),
        branch_id: branch.id,
        responsible_id: null,                              // ← без менеджера
        source_id: src.id,
        type_tag: cfg.b2b ? 'юр.лицо' : 'физ.лицо',
        status,
        funnel_status: cfg.funnel,
        created_at: startAt - 4 * 24 * 3600_000,
        taken_at: startAt - 4 * 24 * 3600_000,
        payment: {
          prepayment_amount: prepay,
          prepayment_receipts: [],
          remainder_amount: remainder,
          remainder_receipts: [],
        },
        event: {
          occasion_id: occ.id,
          event_date: dayBase,
          start_at: startAt, end_at: endAt,
          place: i % 2 === 0 ? 'studio' : 'outside',
          address: i % 2 === 0 ? '' : `${branch.name}, ул. ${pick(['Абая', 'Толе би', 'Кенесары'])}, ${5 + i * 3}`,
          participant_count: 10 + i * 4,
          game_format_id: fmt.id,
          duration_minutes: duration,
          language: pick(['Русский', 'Казахский']),
          emoji: EMOJIS[i % EMOJIS.length],
        },
        transactions: [],
        staff_assignments: [],
        history: [{ ts: startAt - 4 * 24 * 3600_000, action: 'Создано' }],
        team_id: null,
        updated_at: now,
      });
    });
  }

  // Несколько сделок без даты — в очередь
  for (let i = 0; i < 8; i++) {
    dealCounter += 1;
    deals.push({
      id: nanoid(),
      number: String(dealCounter),
      kind: 'b2c',
      currency: 'KZT',
      amount: (Math.floor(Math.random() * 30) + 5) * 10_000,
      contact_name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
      contact_phone: randPhone(),
      branch_id: pick(branches).id,
      responsible_id: null,
      source_id: pick(sources).id,
      type_tag: 'физ.лицо',
      status: 'new',
      created_at: now - i * 3600_000,
      taken_at: now - i * 3600_000,
      payment: { prepayment_receipts: [], remainder_receipts: [] },
      event: {
        occasion_id: pick(occasions).id,
        emoji: pick(EMOJIS),
      },
      transactions: [],
      staff_assignments: [],
      history: [{ ts: now - i * 3600_000, action: 'Создано' }],
      team_id: null,
      updated_at: now,
    });
  }

  await db.deals.bulkPut(deals);
}
